/* eslint-disable prettier/prettier */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKPagination from "components/MKPagination";
import Icon from "@mui/material/Icon";

import blogPosts from "pages/Blog/components/blogPosts";

const POSTS_PER_PAGE = 10;

function BlogCard({ post }) {
    return (
        <MKBox
            component={Link}
            to={`/blog/${post.id}`}
            sx={{
                display: "block",
                textDecoration: "none",
                height: "100%",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                transition: "transform 0.22s ease, box-shadow 0.22s ease",
                backgroundColor: "#fff",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                },
            }}
        >
            {/* Image */}
            <MKBox
                sx={{
                    width: "100%",
                    height: 200,
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <MKBox
                    component="img"
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "scale(1.04)" },
                    }}
                />
                {post.category && (
                    <MKBox
                        sx={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            background: "rgba(26,115,232,0.88)",
                            color: "#fff",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: "20px",
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                        }}
                    >
                        {post.category}
                    </MKBox>
                )}
            </MKBox>

            {/* Body */}
            <MKBox sx={{ p: { xs: 2, md: 2.5 } }}>
                {post.date && (
                    <MKTypography
                        variant="caption"
                        color="secondary"
                        sx={{ display: "block", mb: 0.5, opacity: 0.7 }}
                    >
                        {new Date(post.date).toLocaleDateString("ro-RO", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        })}
                    </MKTypography>
                )}
                <MKTypography
                    variant="h6"
                    color="dark"
                    sx={{
                        fontWeight: 700,
                        lineHeight: 1.35,
                        mb: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {post.title}
                </MKTypography>
                <MKTypography
                    variant="body2"
                    color="text"
                    sx={{
                        opacity: 0.8,
                        lineHeight: 1.65,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {post.description}
                </MKTypography>
                <MKBox
                    sx={{
                        mt: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "#1a73e8",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                    }}
                >
                    Citește articolul
                    <Icon sx={{ fontSize: "1rem !important" }}>arrow_forward</Icon>
                </MKBox>
            </MKBox>
        </MKBox>
    );
}

BlogCard.propTypes = {
    post: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        date: PropTypes.string,
        category: PropTypes.string,
    }).isRequired,
};

function BlogList() {
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(blogPosts.length / POSTS_PER_PAGE);

    const startIdx = (currentPage - 1) * POSTS_PER_PAGE;
    const visiblePosts = blogPosts.slice(startIdx, startIdx + POSTS_PER_PAGE);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const canonicalUrl = "https://doral-cleaning.ro/blog/";
    const metaTitle = "Blog Doral Cleaning – Sfaturi și Ghiduri de Curățenie Profesională";
    const metaDescription =
        "Articole utile despre curățenie profesională, îngrijirea suprafețelor, tapițerie, geamuri și mai mult. Sfaturi de la experții Doral Cleaning.";

    return (
        <>
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta
                    property="og:image"
                    content="https://doral-cleaning.ro/static/media/9-1-vitrate.706d0ff0aa7ab503bdad.webp"
                />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Blog",
                        name: "Blog Doral Cleaning",
                        url: canonicalUrl,
                        description: metaDescription,
                        publisher: {
                            "@type": "Organization",
                            name: "Doral Cleaning",
                            logo: {
                                "@type": "ImageObject",
                                url: "https://doral-cleaning.ro/static/media/logo.webp",
                            },
                        },
                    })}
                </script>
            </Helmet>

            {/* Hero */}
            <MKBox
                sx={{
                    background: "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
                    pt: { xs: 10, md: 12 },
                    pb: { xs: 6, md: 8 },
                    textAlign: "center",
                    px: 2,
                }}
            >
                <MKTypography
                    variant="h1"
                    sx={{
                        color: "#fff",
                        fontSize: { xs: "2rem", md: "2.8rem" },
                        fontWeight: 800,
                        mb: 1.5,
                    }}
                >
                    Blog Doral Cleaning
                </MKTypography>
                <MKTypography
                    variant="body1"
                    sx={{
                        color: "rgba(255,255,255,0.88)",
                        maxWidth: 580,
                        mx: "auto",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                    }}
                >
                    Sfaturi practice, ghiduri și noutăți despre curățenie profesională de la echipa
                    Doral Cleaning
                </MKTypography>
            </MKBox>

            {/* Articles grid */}
            <MKBox
                component="main"
                sx={{ bgcolor: "#f5f7fb", minHeight: "60vh", py: { xs: 5, md: 7 } }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={3}>
                        {visiblePosts.map((post) => (
                            <Grid item xs={12} sm={6} lg={4} key={post.id}>
                                <BlogCard post={post} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <MKBox sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
                            <MKPagination variant="gradient" color="info">
                                <MKPagination
                                    item
                                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <Icon>keyboard_arrow_left</Icon>
                                </MKPagination>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <MKPagination
                                        item
                                        key={page}
                                        active={page === currentPage}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </MKPagination>
                                ))}

                                <MKPagination
                                    item
                                    onClick={() =>
                                        handlePageChange(Math.min(totalPages, currentPage + 1))
                                    }
                                    disabled={currentPage === totalPages}
                                >
                                    <Icon>keyboard_arrow_right</Icon>
                                </MKPagination>
                            </MKPagination>
                        </MKBox>
                    )}

                    {/* Post count info */}
                    <MKTypography
                        variant="caption"
                        color="secondary"
                        sx={{ display: "block", textAlign: "center", mt: 2, opacity: 0.6 }}
                    >
                        {blogPosts.length === 1 ? "1 articol" : `${blogPosts.length} articole`}
                        {totalPages > 1 && ` · Pagina ${currentPage} din ${totalPages}`}
                    </MKTypography>
                </Container>
            </MKBox>
        </>
    );
}

export default BlogList;
